import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LotCreateRequest,
  LotListQuery,
  LotListResponse,
  LotResponse,
  LotUpdateRequest,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<LotListQuery>) => ['lots', 'list', q] as const,
  one: (id: string) => ['lots', 'one', id] as const,
};

function toQueryString(q: Partial<LotListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.search) params.set('search', q.search);
  if (q.status) params.set('status', q.status);
  if (q.origin) params.set('origin', q.origin);
  if (q.productId) params.set('productId', q.productId);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useLots(query: Partial<LotListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<LotListResponse>(`/api/lots${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useLot(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['lots', 'one', 'NIL'],
    queryFn: () => api<LotResponse>(`/api/lots/${id}`),
    enabled: !!id,
  });
}

export function useCreateLot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: LotCreateRequest) =>
      api<LotResponse>('/api/lots', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lots', 'list'] });
    },
  });
}

export function useUpdateLot(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: LotUpdateRequest) =>
      api<LotResponse>(`/api/lots/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['lots', 'list'] });
    },
  });
}

export function useDeleteLot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/lots/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lots', 'list'] });
    },
  });
}
